import React, { useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useStudio3DData } from '../../hooks/useStudio3DData'
import { useStudioEngine } from '../../hooks/useStudioEngine'
import { useRealisticHallScene } from './useRealisticHallScene'
import { HallPrintPhoto } from './buildRealisticHall'
import { EgowallGlyph, StudioLoadingVeil, STUDIO_LOAD_BG } from '../../components/StudioLoadingVeil'
import { GlassPanel, monoLabelSx, STUDIO_ACCENT } from '../../components/shared'
import { APP_NAME } from '@/utils/constants'

/**
 * `/galleries/:galleryId/present-realistic` — walks a gallery's real photos
 * through a cinematic, walkable museum hall instead of the flat editor view.
 *
 * A sibling to Studio, not a replacement — Studio stays the editor
 * (freeform hang, inspector, autosave); this route is read-mostly ("how
 * would this gallery feel as a room"), reachable from Studio's top bar.
 * Nothing here persists back to the gallery topic.
 *
 * v1 scope: fixed 7 print slots, no Classic-room/multi-room support, no
 * guided tour yet.
 */
export function PresentRealisticScreen() {
  const { galleryId = '' } = useParams<{ galleryId: string }>()
  const navigate = useNavigate()
  const data = useStudio3DData(galleryId)
  const ready = data.seedReady

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { scene, engine } = useStudioEngine(canvasRef, ready)

  // Real hung prints (same shape Studio's lightbox uses) — falls back to a
  // diverse demo set (not just wedding photos) when the gallery has none yet.
  const photos: HallPrintPhoto[] = useMemo(() => {
    const real = data.initialArts
      .filter((a) => !a.isPlaceholder && a.img)
      .map((a) => ({ url: a.img, ratio: a.ratio || 4 / 3, name: a.name }))
    if (real.length > 0) return real
    return FALLBACK_PHOTOS
  }, [data.initialArts])

  const hall = useRealisticHallScene(scene, engine, canvasRef, photos)

  const backToStudio = () => navigate(`/galleries/${galleryId}/studio`)

  useEffect(() => {
    if (!hall.ready) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !hall.insideMode) hall.playIntro(false)
      if (e.key === 'Escape' && hall.insideMode) hall.stepOutside()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hall.ready, hall.insideMode])

  if (data.isError) {
    return (
      <Stage sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: 15, fontWeight: 600, mb: 1 }}>Gallery not found</Box>
          <Box sx={{ fontSize: 13, color: 'rgba(244,243,246,0.55)' }}>
            This route expects a real GALLERY topic id — open one from /galleries first.
          </Box>
        </Box>
      </Stage>
    )
  }

  return (
    <Stage>
      <Canvas ref={canvasRef} id='present-realistic-canvas' />

      {(!ready || !hall.ready) && <StudioLoadingVeil />}

      <BackLink onClick={backToStudio}>← BACK TO STUDIO</BackLink>

      <Brand>
        <EgowallGlyph small />
        <Typography component='span' sx={{ fontWeight: 750, letterSpacing: '-0.02em', fontSize: 15, textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
          {APP_NAME}
        </Typography>
        <Typography component='span' sx={{ ...monoLabelSx, fontSize: 8, ml: 0.25, textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          {data.galleryTitle.toUpperCase()}
        </Typography>
      </Brand>

      <TagReal>
        <Dot />
        <span>LIVE BABYLON.JS · PBR + IBL · MIRROR FLOOR · REAL-TIME SHADOWS — NOT A RENDER</span>
      </TagReal>

      {ready && hall.ready && !hall.insideMode && (
        <Cine>
          <Typography sx={{ ...monoLabelSx, fontSize: 9 }}>A PREVIEW OF WHAT&apos;S POSSIBLE</Typography>
          <Typography sx={{ fontSize: 'clamp(19px, 2.6vw, 27px)', fontWeight: 750, letterSpacing: '-0.02em', textAlign: 'center', textShadow: '0 6px 30px rgba(0,0,0,0.65)' }}>
            Every album, a room worth walking into.
          </Typography>
          <EnterBtn onClick={() => hall.playIntro(false)}>
            Walk in <Kbd sx={{ color: '#17130a', background: 'rgba(23,19,10,0.18)' }}>↵ ENTER</Kbd>
          </EnterBtn>
        </Cine>
      )}

      {hall.insideMode && (
        <Hud>
          <span>
            <Kbd>DRAG</Kbd> LOOK · <Kbd>W A S D</Kbd> WALK
          </span>
          <OutsideBtn onClick={() => hall.stepOutside()}>STEP OUTSIDE</OutsideBtn>
        </Hud>
      )}

      {hall.insideMode && (
        <Credits>
          <b>SCENE</b> — procedural PBR architecture, no imported meshes
          <br />
          <b>LIGHT</b> — IBL + directional sun + spotlit track fixtures, real-time shadows
          <br />
          <b>FLOOR</b> — live MirrorTexture reflection
          <br />
          <b>POST</b> — SSAO2 · bloom · ACES tonemap · dust in the skylight shaft
          <br />
          <b>PHOTOS</b> — {data.hasSourcePhotos ? 'this gallery' : 'sample set'}, same asset pipeline as the Studio
        </Credits>
      )}
    </Stage>
  )
}

const FALLBACK_PHOTOS: HallPrintPhoto[] = [
  { url: '/marketing/landing-v2/a02-beach.jpg', ratio: 3 / 4 },
  { url: '/marketing/landing-v2/a06-baby.jpg', ratio: 3 / 4 },
  { url: '/marketing/landing-v2/a03-mountains.jpg', ratio: 4 / 3 },
  { url: '/marketing/landing-v2/a04-dog.jpg', ratio: 4 / 3 },
  { url: '/marketing/landing-v2/a07-city.jpg', ratio: 3 / 4 },
  { url: '/marketing/landing-v2/a08-coffee.jpg', ratio: 4 / 3 },
  { url: '/marketing/landing-v2/a05-birthday.jpg', ratio: 4 / 3 },
]

const Stage = styled(Box)({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  background: STUDIO_LOAD_BG,
  color: '#f4f3f6',
  fontFamily: 'var(--ds-font)',
})

const Canvas = styled('canvas')({
  position: 'fixed',
  inset: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  touchAction: 'none',
  display: 'block',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
})

const BackLink = styled('button')({
  all: 'unset',
  position: 'fixed',
  top: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 40,
  cursor: 'pointer',
  ...monoLabelSx,
  fontSize: 8.5,
  textShadow: '0 2px 12px rgba(0,0,0,0.6)',
  '&:hover': { color: 'var(--ds-night-ink, #f4f3f6)' },
})

const Brand = styled(Box)({
  position: 'fixed',
  top: 18,
  left: 22,
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 9,
})

const TagReal = styled(Box)({
  position: 'fixed',
  top: 19,
  right: 22,
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 27,
  padding: '0 12px',
  borderRadius: 999,
  background: 'rgba(19,18,25,0.55)',
  border: '1px solid rgba(244,243,246,0.12)',
  backdropFilter: 'blur(10px)',
  ...monoLabelSx,
  fontSize: 8,
  color: 'rgba(244,243,246,0.5)',
})

const Dot = styled('i')({
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: '#58d68d',
  boxShadow: '0 0 8px #58d68d',
  flexShrink: 0,
})

const Cine = styled(Box)({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 44,
  zIndex: 40,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
})

const EnterBtn = styled('button')({
  all: 'unset',
  height: 42,
  padding: '0 22px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 650,
  color: '#17130a',
  background: 'var(--ds-gradient-spectrum)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
  transition: 'transform 160ms ease, box-shadow 160ms ease',
  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 18px 46px rgba(0,0,0,0.5)' },
})

const Kbd = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.08em',
  borderRadius: 5,
  padding: '2px 6px',
  display: 'inline-block',
  background: 'rgba(244,243,246,0.06)',
  border: '1px solid rgba(244,243,246,0.14)',
  color: 'rgba(244,243,246,0.85)',
})

const Hud = styled(GlassPanel)({
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  zIndex: 40,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 8px 8px 16px',
  borderRadius: 999,
  ...monoLabelSx,
  fontSize: 8.5,
})

const OutsideBtn = styled('button')({
  all: 'unset',
  height: 26,
  padding: '0 11px',
  borderRadius: 8,
  color: STUDIO_ACCENT.violet,
  fontWeight: 700,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  background: 'rgba(190,157,253,0.1)',
  border: '1px solid rgba(190,157,253,0.3)',
  cursor: 'pointer',
  '&:hover': { background: 'rgba(190,157,253,0.18)' },
})

const Credits = styled(Box)({
  position: 'fixed',
  left: 22,
  bottom: 24,
  zIndex: 40,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 7.5,
  letterSpacing: '0.07em',
  color: 'rgba(244,243,246,0.55)',
  lineHeight: 1.7,
  maxWidth: 300,
  opacity: 0.85,
  '& b': { color: 'rgba(244,243,246,0.75)' },
})
